import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Script from 'next/script';
import { stub as $t } from '@nbcnews/analytics-framework';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { useVertical } from 'lib/Hooks/useVertical';
import { AUTHENTICATED } from 'lib/myNewsConstants';
import { PIANO } from 'lib/brandFeatures';
import { useBedrockRegistration } from 'store';
import { PIANO_URI, getPianoAid, PIANO_ENV } from './pianoConfig';
import PianoWrapper from './PianoWrapper';
import { flushTpCommands } from './PianoTagQueue';
import Piano from '../../components/Piano/Piano';

const TODAY_LD_FLAG = 'piano-today';
const NEWS_LD_FLAG = 'nbc-news-subscriptions';

$t('register', 'ramen_pianoflow');

const PianoContext = createContext(null);

/**
 * Custom hook to access the PianoContext
 * @returns {any} The current value of the PianoContext.
 * @throws {Error} If used outside of a PianoContext provider.
 */
export const usePiano = () => useContext(PianoContext);

/**
 * PianoProvider injects the Piano JS SDK and keeps its auth state in-sync
 * with Bedrock (both the zustand store and the auth cookie).
 *
 * @param {object}   props
 * @param {ReactNode} props.children  React subtree to wrap
 * @returns {JSX.Element}
 */
// eslint-disable-next-line import/no-default-export
export default function PianoProvider({ children }) {
  const { vertical } = useVertical();
  const flags = useFeatureFlagContext();
  const flagEnabled = flags?.[TODAY_LD_FLAG] || flags?.[NEWS_LD_FLAG];
  const pianoEnabled = !!flagEnabled && getFeatureConfigForBrand(PIANO, vertical);

  const brandAid = useMemo(() => getPianoAid(PIANO_ENV, vertical), [vertical]);
  const scriptSrc = brandAid ? `${PIANO_URI}/xbuilder/experience/load?aid=${brandAid}` : null;

  const authenticationState = useBedrockRegistration((s) => s.authenticationState);
  const showAuthenticationModal = useBedrockRegistration((s) => s.showAuthenticationModal);
  const openAuthModal = useBedrockRegistration((s) => s.setAuthenticationModal);
  const pianoToken = useBedrockRegistration((s) => s.piano_token);
  const showGate = useBedrockRegistration((s) => s.showGate);
  const setCheckoutFlow = useBedrockRegistration((s) => s.setCheckoutFlow);

  const authenticate = useCallback(() => {
    if (
      authenticationState !== AUTHENTICATED
      && !showAuthenticationModal
    ) {
      openAuthModal(true);
    }
  }, [authenticationState, showAuthenticationModal, openAuthModal]);

  const {
    tpPush,
    setUserReference,
    waitForSDK,
  } = PianoWrapper;

  const [sdkReady, setSdkReady] = useState(false);

  // Handle window messages from Piano templates
  useEffect(() => {
    if (!pianoEnabled) return () => { };


    /**
     * Handles incoming messages and performs actions based on the message data.
     * These are triggered from the Piano integration script
     *
     * @param {MessageEvent} event - The message event object containing the data.
     * @param {string} event.data - The data sent with the message event.
     */
    const handleMessage = (event) => {
      const payload = event.data || {};

      if (payload.type === 'OPEN_AUTH_MODAL') {
        // Close Piano modals BEFORE opening auth
        window.tp?.modal?.close?.();
        window.tp?.offer?.close?.();
        setCheckoutFlow(true);
        authenticate();
      }

      if (payload.type === 'CHECKOUT_COMPLETE') {
        // TO DO: add preferences modal trigger here
      }

      if (payload.type === 'TRACK_EVENT') {
        $t('track', 'ramen_pianoflow', event.payload);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pianoEnabled, authenticate]);

  useEffect(() => {
    if (!pianoEnabled) return;

    waitForSDK().then(() => {
      setSdkReady(true);
    });
  }, [pianoEnabled]);

  const syncAuthState = useCallback(
    (token) => {
      if (!pianoEnabled || !sdkReady) return;

      if (token === window.tp?.externalJWT) return;
      PianoWrapper.setUserAndReload(token || '');
    },
    [pianoEnabled, sdkReady],
  );

  useEffect(() => {
    if (pianoEnabled && sdkReady) {
      flushTpCommands(tpPush);
      syncAuthState(pianoToken);
    }
  }, [pianoEnabled, sdkReady, pianoToken, syncAuthState]);

  // resume the checkout flow if the user is authenticated
  useEffect(() => {
    if (authenticationState !== AUTHENTICATED || !sdkReady || showGate) return;

    // eslint-disable-next-line no-underscore-dangle
    if (window.__pendingCheckout && window.tp.externalJWT) {
      // all the data is set, so send back to Piano to relaunch the checkout
      window.postMessage('PIANO_USER_AUTHENTICATED', '*');
    }
  }, [authenticationState, pianoToken, sdkReady, showGate]);

  const api = useMemo(
    () => Object.freeze({
      pianoEnabled,
      push: tpPush,
      setUserRef: setUserReference,
      authenticate,
    }),
    [pianoEnabled, tpPush, setUserReference, authenticate],
  );

  return (
    <>
      {pianoEnabled && scriptSrc && (
        <>
          <Script
            id="piano-pre"
            strategy="beforeInteractive"
            dangerouslySetInnerHTML={{
              __html: 'window.tp = window.tp || [];',
            }}
          />

          <Script
            id="piano-runtime"
            strategy="beforeInteractive"
            src={scriptSrc}
          />
        </>
      )}

      <PianoContext.Provider value={api}>
        {pianoEnabled && sdkReady && <Piano />}
        {children}
      </PianoContext.Provider>
    </>
  );
}
